import React from 'react';
import { Box, Typography, Container, Grid } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';

const AnalyzeInfluencers = () => {
    return (
        <Box className="bg-white min-h-screen font-sans text-gray-900 pt-20">
            {/* 1. Hero Section */}
            <Container maxWidth="lg" sx={{ pt: 6, pb: 10 }}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <div className="inline-block px-4 py-1.5 rounded-full bg-pink-50 text-pink-600 font-bold text-xs uppercase tracking-wide mb-6 animate-fade-in-up">
                            Data & Insights
                        </div>
                        <Typography variant="h1" className="text-4xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-6 animate-fade-in-up delay-100">
                            Marketing Decisions <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-rose-600">Backed by Data</span>
                        </Typography>
                        <Typography variant="body1" className="text-lg text-gray-600 mb-8 leading-relaxed animate-fade-in-up delay-200">
                            Go beyond vanity metrics. Analyze audience quality, detect fraud, and measure real ROI with enterprise-grade analytics.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        {/* Abstract Visual - Charts */}
                        <Box className="relative w-full aspect-square md:aspect-[4/3] animate-float">
                            <div className="absolute inset-0 bg-gradient-to-br from-pink-50 to-rose-50 rounded-3xl border border-white shadow-2xl overflow-hidden flex items-center justify-center">
                                {/* Background Elements */}
                                <div className="absolute inset-0 opacity-60">
                                    <div className="absolute top-[20%] right-[20%] w-48 h-48 bg-pink-400/20 rounded-full blur-3xl animate-pulse-slow"></div>
                                    <div className="absolute bottom-[20%] left-[20%] w-32 h-32 bg-rose-400/20 rounded-full blur-3xl animate-pulse-slow delay-1000"></div>
                                </div>

                                {/* Center UI Element: Chart Card */}
                                <div className="relative z-10 w-3/4 bg-white/60 backdrop-blur-md border border-white/80 p-6 rounded-2xl shadow-xl">
                                    <div className="flex justify-between items-end h-32 gap-3 mb-2">
                                        {[40, 70, 50, 90, 60, 85].map((h, i) => (
                                            <div key={i} className="w-full bg-gradient-to-t from-pink-500 to-rose-400 rounded-t-md opacity-90" style={{ height: `${h}%` }}></div>
                                        ))}
                                    </div>
                                    <div className="h-0.5 bg-gray-300 w-full mb-4"></div>
                                    <div className="flex justify-between text-xs font-bold text-gray-500 uppercase">
                                        <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span>
                                    </div>

                                    {/* Floating Badge */}
                                    <div className="absolute top-4 right-4 bg-white p-2 rounded-lg shadow-sm border border-gray-100">
                                        <div className="text-xs text-gray-500">Growth</div>
                                        <div className="text-lg font-bold text-green-500">+124%</div>
                                    </div>
                                </div>

                                {/* Floating Elements */}
                                <div className="absolute top-[10%] left-[5%] bg-white p-3 rounded-xl shadow-lg animate-bounce-custom delay-100">
                                    <PieChartIcon className="text-purple-500" />
                                </div>
                                <div className="absolute bottom-[10%] right-[10%] bg-white p-3 rounded-xl shadow-lg animate-bounce-custom delay-500">
                                    <ShieldIcon className="text-blue-500" />
                                </div>
                            </div>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* 2. Features Grid */}
            <Container maxWidth="lg" sx={{ py: 16 }}>
                <div className="text-center mb-12">
                    <Typography variant="h2" className="text-3xl lg:text-4xl font-bold text-gray-900">
                        Deep Dive Analytics
                    </Typography>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Feature 1: Audience Insight (Large) */}
                    <div className="md:col-span-2 bg-rose-950 rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden group hover:shadow-2xl hover:shadow-rose-900/20 transition-all duration-500 cursor-pointer">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500 rounded-full mix-blend-overlay filter blur-3xl opacity-20 group-hover:opacity-40 transition-opacity duration-500"></div>
                        <div className="relative z-10">
                            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                                <DonutLargeIcon className="text-white" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4">Audience Demographics</h3>
                            <p className="text-rose-200 mb-6 max-w-md group-hover:text-white transition-colors">
                                Understand exactly who follows an influencer. Break down by age, gender, location (city/country), and interests.
                            </p>
                        </div>
                    </div>

                    {/* Feature 2: Fraud Detection */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-red-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mb-6 text-red-600 group-hover:bg-red-600 group-hover:text-white transition-colors">
                            <ShieldIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Fraud Detection</h3>
                        <p className="text-gray-500 text-sm">Spot fake followers and engagement bots instantly. Ensure your budget goes to real people.</p>
                    </div>

                    {/* Feature 3: ROI Tracking */}
                    <div className="bg-white border border-gray-200 rounded-3xl p-8 hover:border-green-400 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                        <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mb-6 text-green-600 group-hover:bg-green-600 group-hover:text-white transition-colors">
                            <TrendingUpIcon />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">ROI Measurement</h3>
                        <p className="text-gray-500 text-sm">Track clicks, conversions, and sales generated by each creator. Connect to Shopify/WooCommerce.</p>
                    </div>

                    {/* Feature 4: Competitor Analysis (Wide) */}
                    <div className="md:col-span-2 bg-gray-50 rounded-3xl p-8 lg:p-10 border border-gray-100 hover:border-gray-300 transition-all flex flex-col md:flex-row items-center gap-8 group">
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Brand Affinity & Competitors</h3>
                            <p className="text-gray-600 text-sm mb-4">See which other brands the creator has worked with. Check for conflicts of interest or brand alignment.</p>
                            <ul className="space-y-2 text-sm text-gray-500">
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-pink-500" /> Past collaboration history</li>
                                <li className="flex items-center gap-2"><CheckCircleIcon fontSize="small" className="text-pink-500" /> Brand safety check</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </Container>
        </Box>
    );
};

export default AnalyzeInfluencers;
